import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { allMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

const categoryLabelMap: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
  anime: '动漫',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = allMovies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <>
        <Navbar />
        <ScrollToTop />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
            </div>
            <h1 className="text-lg font-heading font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您要查看的影片不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = categoryLabelMap[movie.category] || movie.category;
  const relatedMovies = allMovies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${movie.title} - 窝窝影院免费在线观看`,
    description: movie.description,
    image: movie.image,
    datePublished: `${movie.year}-01-01`,
    dateModified: '2026-01-01',
    author: {
      '@type': 'Organization',
      name: '窝窝影院',
    },
    publisher: {
      '@type': 'Organization',
      name: '窝窝影院',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.wowocinema.com/article/${movie.id}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${movie.title}可以在窝窝影院免费观看吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的，《${movie.title}》在窝窝影院提供免费在线观看，无需注册付费，高清画质流畅播放不缓冲。`,
        },
      },
      {
        '@type': 'Question',
        name: `${movie.title}的评分和口碑如何？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${movie.title}》在窝窝影院的评分为${movie.rating}分，由${movie.director}执导，${movie.actors.slice(0, 3).join('、')}等主演，深受观众喜爱。`,
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Navbar />
      <ScrollToTop />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryLabel, path: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="relative overflow-hidden rounded-lg aspect-[2/3]">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      className="w-full h-full object-cover object-top"
                      title={`${movie.title} - ${movie.subtitle}`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{movie.subtitle}</p>

                <div className="flex items-center gap-2 flex-wrap mb-6">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-accent-100 text-accent-800 text-sm rounded-lg whitespace-nowrap">
                    <i className="ri-star-fill"></i>
                    {movie.rating}
                  </span>
                  {movie.isNew && (
                    <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-lg whitespace-nowrap">
                      最新更新
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="inline-block px-3 py-1 bg-accent-500/10 text-accent-600 text-sm rounded-lg whitespace-nowrap">
                      <i className="ri-fire-line mr-1"></i>热门
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 p-4 bg-background-100 rounded-lg">
                  {[
                    { label: '导演', value: movie.director },
                    { label: '主演', value: movie.actors.join(' / ') },
                    { label: '类型', value: movie.subcategory },
                    { label: '地区', value: movie.region },
                    { label: '语言', value: movie.language },
                    { label: '时长', value: movie.duration },
                    { label: '年份', value: String(movie.year) },
                    { label: '状态', value: movie.updateInfo },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap min-w-[40px]">
                        {item.label}：
                      </span>
                      <span className="text-xs text-foreground-800">{item.value}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-6">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-3 mb-8">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-4 py-3 bg-background-100 text-foreground-700 text-sm rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                </div>

                <div className="prose prose-sm max-w-none">
                  <h2 className="text-base font-heading font-semibold text-foreground-900 mb-3">
                    剧情简介
                  </h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
                  <div
                    className="mt-6 text-sm text-foreground-700 leading-relaxed space-y-4"
                    dangerouslySetInnerHTML={{ __html: movie.content }}
                  />
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} variant="poster" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}